import React, { useState, useEffect, useCallback } from 'react';
import { getVisitRequests, createVisitRequest, updateVisitRequest, deleteVisitRequest } from '../../services/api';

const STATUSES = ['Submitted', 'Approved', 'Denied'];

const STATUS_COLORS = {
  Submitted: 'var(--status-active)',
  Approved: 'var(--status-completed)',
  Denied: 'var(--status-critical)',
};

const EMPTY_FORM = {
  visitor_name: '',
  company: '',
  date_of_visit: '',
  time_of_visit: '',
  sponsor_poc: '',
  nationality: '',
  status: 'Submitted',
  purpose: '',
  notes: '',
};

export default function VisitRequests({ onNavigate }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Sorting
  const [sortKey, setSortKey] = useState('date_of_visit');
  const [sortDir, setSortDir] = useState('desc');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getVisitRequests();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load visit records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // --- Filtering & Sorting ---
  const filtered = records.filter((r) => {
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (r.visitor_name || '').toLowerCase().includes(q) ||
        (r.company || '').toLowerCase().includes(q) ||
        (r.sponsor_poc || '').toLowerCase().includes(q) ||
        (r.nationality || '').toLowerCase().includes(q) ||
        (r.purpose || '').toLowerCase().includes(q) ||
        (r.notes || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey] || '';
    const bv = b[sortKey] || '';
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  // --- Modal ---
  const openCreate = () => {
    setEditingRecord(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setApiError('');
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditingRecord(record);
    setForm({
      visitor_name: record.visitor_name || '',
      company: record.company || '',
      date_of_visit: record.date_of_visit || '',
      time_of_visit: record.time_of_visit || '',
      sponsor_poc: record.sponsor_poc || '',
      nationality: record.nationality || '',
      status: record.status || 'Submitted',
      purpose: record.purpose || '',
      notes: record.notes || '',
    });
    setFormErrors({});
    setApiError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.visitor_name.trim()) errs.visitor_name = 'Required';
    if (!form.company.trim()) errs.company = 'Required';
    if (!form.date_of_visit) errs.date_of_visit = 'Required';
    if (!form.sponsor_poc.trim()) errs.sponsor_poc = 'Required';
    if (!form.status) errs.status = 'Required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      if (editingRecord) {
        // Build delta — only send changed fields
        const delta = {};
        Object.keys(form).forEach((key) => {
          if (form[key] !== (editingRecord[key] || '')) {
            delta[key] = form[key];
          }
        });
        if (Object.keys(delta).length === 0) {
          closeModal();
          return;
        }
        await updateVisitRequest(editingRecord.visit_id, delta);
      } else {
        await createVisitRequest(form);
      }
      closeModal();
      await fetchRecords();
    } catch (err) {
      setApiError(err.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingRecord) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete the visit record for "${editingRecord.visitor_name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      await deleteVisitRequest(editingRecord.visit_id);
      closeModal();
      await fetchRecords();
    } catch (err) {
      setApiError(err.message || 'Failed to delete record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuickStatus = async (record, newStatus) => {
    try {
      await updateVisitRequest(record.visit_id, { status: newStatus });
      await fetchRecords();
    } catch (err) {
      setError(err.message || `Failed to ${newStatus.toLowerCase()} visit`);
    }
  };

  // --- Summary ---
  const summary = {
    total: records.length,
    submitted: records.filter((r) => r.status === 'Submitted').length,
    approved: records.filter((r) => r.status === 'Approved').length,
    denied: records.filter((r) => r.status === 'Denied').length,
  };

  // --- Render ---
  if (loading) {
    return (
      <div style={{ color: 'var(--bcore-text-dim)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
        Loading visit records...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'var(--status-critical)', textAlign: 'center', paddingTop: '60px', fontFamily: 'JetBrains Mono, monospace' }}>
        {error}
        <br />
        <button className="btn btn-outline" style={{ marginTop: '16px' }} onClick={fetchRecords}>Retry</button>
      </div>
    );
  }

  return (
    <div className="contracts-page" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
      {/* Summary Bar */}
      <div className="contracts-summary">
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Total</span>
          <span className="contracts-summary-value">{summary.total}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Submitted</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-active)' }}>{summary.submitted}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Approved</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-completed)' }}>{summary.approved}</span>
        </div>
        <div className="contracts-summary-item">
          <span className="contracts-summary-label">Denied</span>
          <span className="contracts-summary-value" style={{ color: 'var(--status-critical)' }}>{summary.denied}</span>
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Search by visitor, company, sponsor, or notes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: '1 1 240px', minWidth: '200px' }}
        />
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="All">All Statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="btn btn-primary" onClick={openCreate}>
          + New Visit
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('visitor_name')}>Visitor{sortIndicator('visitor_name')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('company')}>Company{sortIndicator('company')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_of_visit')}>Date{sortIndicator('date_of_visit')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('time_of_visit')}>Time{sortIndicator('time_of_visit')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('sponsor_poc')}>Sponsor/POC{sortIndicator('sponsor_poc')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('nationality')}>Nationality{sortIndicator('nationality')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', color: 'var(--bcore-text-dim)', padding: '40px' }}>
                  {records.length === 0 ? 'No visit records yet. Click "+ New Visit" to add one.' : 'No records match your filters.'}
                </td>
              </tr>
            ) : sorted.map((r) => (
              <React.Fragment key={r.visit_id}>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === r.visit_id ? null : r.visit_id)}
                >
                  <td style={{ fontWeight: 600 }}>{r.visitor_name}</td>
                  <td>{r.company}</td>
                  <td>{r.date_of_visit || '\u2014'}</td>
                  <td>{r.time_of_visit || '\u2014'}</td>
                  <td>{r.sponsor_poc || '\u2014'}</td>
                  <td>{r.nationality || '\u2014'}</td>
                  <td>
                    <span className="badge-status" style={{
                      background: STATUS_COLORS[r.status] || 'var(--bcore-text-dim)',
                      color: '#000',
                      padding: '2px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}>
                      {r.status}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {r.status === 'Submitted' && (
                        <>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'var(--status-completed)', color: '#000', border: 'none', fontWeight: 600 }}
                            onClick={(e) => { e.stopPropagation(); handleQuickStatus(r, 'Approved'); }}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ background: 'var(--status-critical)', color: '#000', border: 'none', fontWeight: 600 }}
                            onClick={(e) => { e.stopPropagation(); handleQuickStatus(r, 'Denied'); }}
                          >
                            Deny
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={(e) => { e.stopPropagation(); openEdit(r); }}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === r.visit_id && (
                  <tr className="expanded-row">
                    <td colSpan={8}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', padding: '12px 8px' }}>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Purpose</div>
                          <div>{r.purpose || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Created By</div>
                          <div>{r.createdBy || '\u2014'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Last Updated</div>
                          <div>{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : '\u2014'}</div>
                        </div>
                        {r.notes && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--bcore-text-dim)', marginBottom: '4px' }}>Notes</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{r.notes}</div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--bcore-text-dim)' }}>
        Showing {sorted.length} of {records.length} record{records.length !== 1 ? 's' : ''}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
        }} onClick={closeModal}>
          <div
            style={{
              background: 'var(--bcore-bg-secondary, #242424)',
              borderRadius: '12px',
              padding: '32px',
              width: '100%',
              maxWidth: '640px',
              maxHeight: '85vh',
              overflowY: 'auto',
              border: '1px solid var(--bcore-border, #333)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              fontFamily: 'Kode Mono, monospace',
              fontSize: '18px',
              textTransform: 'uppercase',
              marginBottom: '24px',
              color: 'var(--bcore-text-primary)',
            }}>
              {editingRecord ? 'Edit Visit Request' : 'New Visit Request'}
            </h2>

            {apiError && (
              <div style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid var(--status-critical)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: 'var(--status-critical)', fontSize: '13px' }}>
                {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Row 1: Visitor Name + Company */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Visitor Name <span className="required">*</span></label>
                  <input className="form-input" value={form.visitor_name} onChange={(e) => handleChange('visitor_name', e.target.value)} />
                  {formErrors.visitor_name && <div className="form-error">{formErrors.visitor_name}</div>}
                </div>
                <div>
                  <label className="form-label">Company <span className="required">*</span></label>
                  <input className="form-input" value={form.company} onChange={(e) => handleChange('company', e.target.value)} />
                  {formErrors.company && <div className="form-error">{formErrors.company}</div>}
                </div>
              </div>

              {/* Row 2: Date + Time */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Date of Visit <span className="required">*</span></label>
                  <input className="form-input" type="date" value={form.date_of_visit} onChange={(e) => handleChange('date_of_visit', e.target.value)} />
                  {formErrors.date_of_visit && <div className="form-error">{formErrors.date_of_visit}</div>}
                </div>
                <div>
                  <label className="form-label">Time of Visit</label>
                  <input className="form-input" type="time" value={form.time_of_visit} onChange={(e) => handleChange('time_of_visit', e.target.value)} />
                </div>
              </div>

              {/* Row 3: Sponsor/POC + Nationality */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Sponsor / POC <span className="required">*</span></label>
                  <input className="form-input" value={form.sponsor_poc} onChange={(e) => handleChange('sponsor_poc', e.target.value)} placeholder="B/CORE point of contact" />
                  {formErrors.sponsor_poc && <div className="form-error">{formErrors.sponsor_poc}</div>}
                </div>
                <div>
                  <label className="form-label">Nationality</label>
                  <input className="form-input" value={form.nationality} onChange={(e) => handleChange('nationality', e.target.value)} placeholder="e.g., US, UK, Canada" />
                </div>
              </div>

              {/* Row 4: Status */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label className="form-label">Status <span className="required">*</span></label>
                  <select className="form-select" value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {formErrors.status && <div className="form-error">{formErrors.status}</div>}
                </div>
                <div>
                  <label className="form-label">Purpose</label>
                  <input className="form-input" value={form.purpose} onChange={(e) => handleChange('purpose', e.target.value)} placeholder="Reason for visit" />
                </div>
              </div>

              {/* Row 5: Notes */}
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label">Notes</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  style={{ resize: 'vertical' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {editingRecord ? (
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ color: 'var(--status-critical)', borderColor: 'var(--status-critical)' }}
                    onClick={handleDelete}
                    disabled={submitting}
                  >
                    Delete Record
                  </button>
                ) : <div />}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button type="button" className="btn btn-outline" onClick={closeModal} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (editingRecord ? 'Saving...' : 'Creating...') : (editingRecord ? 'Save Changes' : 'Create Record')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
